import React, { createContext, useContext, useState, useCallback } from 'react';
import { Toast } from 'devextreme-react/toast';
import IconifyIcon from '../components/icon';
type ToastType = 'info' | 'success' | 'error' | 'warning' | 'loading';
import styles from "./Toast.module.css"; // ✅ css module
interface ToastConfig {
  isVisible: boolean;
  type: ToastType;
  message: string;
}

interface ToastContextProps {
  showToast: (message: string, type?: ToastType) => void;
  hideToast: () => void;
}

const ToastContext = createContext<ToastContextProps | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toastConfig, setToastConfig] = useState<ToastConfig>({
    isVisible: false,
    type: 'info',
    message: '',
  });

  const getIcon = (type: ToastType) => {
    switch (type) {
      case "success":
        return <IconifyIcon icon="mdi:check-circle" className="text-green-500 flex-shrink-0" />;
      case "error":
        return <IconifyIcon icon="mdi:alert-circle" className="text-red-500 flex-shrink-0" />;
      case "loading":
        return <IconifyIcon icon="mdi:loading" className="animate-spin text-blue-500 flex-shrink-0" />;
      case "warning":
        return <IconifyIcon icon="mdi:alert" className="text-yellow-500 flex-shrink-0" />;
      case "info":
      default:
        return <IconifyIcon icon="mdi:information" className="text-blue-500 flex-shrink-0" />;
    }
  };

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    setToastConfig({ isVisible: true, type, message });
  }, []);

  const hideToast = () => setToastConfig(prev => ({ ...prev, isVisible: false }));

  const onHiding = useCallback(() => {
    setToastConfig((prev) => ({ ...prev, isVisible: false }));
  }, []);

const renderContent = () => (
  <div className={`${styles.content} ${styles[toastConfig.type]}`}>
    {getIcon(toastConfig.type)}
    <span>{toastConfig.message}</span>
  </div>
);


  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      <Toast
        visible={toastConfig.isVisible}
        type={toastConfig.type === 'loading' ? 'info' : toastConfig.type}
        onHiding={onHiding}
        displayTime={toastConfig.type === 'loading' ? 0 : 2000} // ⬅ 0 = tidak auto close
        width="auto"
        maxWidth={400}
        position={{
          my: { x: 'right', y: 'top' },
          at: { x: 'right', y: 'top' },
          offset: { x: 0, y: 20 }
        }}
        contentRender={renderContent}
      />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};